import { useCallback, useEffect, useMemo, useState } from 'react';

import { warehouseApi, type WarehouseApiError } from '../api/warehouse.api';
import type { WarehouseSku } from '../types';
import { useLoad } from './useLoad';
import {
  PICKER_PAGE_SIZE,
  type PagedPicker,
  usePagedPicker,
} from './usePagedPicker';

export interface SkuOption {
  value: string;
  label: string;
}

/** Rows per SKU picker page (first page and every search). */
export const SKU_PAGE_SIZE = PICKER_PAGE_SIZE;

const skuKey = (s: WarehouseSku): string => s.sku;

/** `SKU · item name` — the picker option / selected label. */
export const skuLabel = (s: Pick<WarehouseSku, 'sku' | 'itemName'>): string =>
  `${s.sku} · ${s.itemName}`;

const NO_SKUS: WarehouseSku[] = [];

/** Unique, sorted, non-empty codes — a stable request / dependency key. */
const codeKey = (codes: readonly string[]): string =>
  Array.from(new Set(codes.filter(Boolean)))
    .sort()
    .join('\n');
const codesOf = (key: string): string[] => (key ? key.split('\n') : []);

export interface ClientCatalog {
  clientID: number | null;
  /** First page of the client's catalog — the picker list before typing. */
  firstPage: WarehouseSku[];
  /**
   * Every SKU this form knows: the first page, options picked, chosen codes
   * resolved by `skus=`, SKUs created in place.
   */
  skuBy: Map<string, WarehouseSku>;
  /** The client has no SKU at all (and none was created here). */
  empty: boolean;
  /**
   * Allocatable qty in the hub of a chosen SKU; null while unknown (no hub,
   * still loading, or the availability request failed).
   */
  availableFor: (sku: string) => number | null;
  /** Keep a SKU the user picked from a search result. */
  remember: (sku: WarehouseSku) => void;
  /** Put a SKU created in place (PO form) into the catalog without a refetch. */
  addSku: (sku: WarehouseSku) => void;
  loading: boolean;
  /**
   * The catalog request failed — the SKU list is unknown, NOT empty. Forms
   * must say so (with `reload`) instead of offering "no SKUs" / "SKU baru".
   */
  error: WarehouseApiError | null;
  /** The availability request failed — stock is unknown (warning only). */
  stockError: WarehouseApiError | null;
  reload: () => void;
}

interface Options {
  clientID: number | null;
  /** Hub for the "tersedia" column; null = no availability (PO). */
  hubID: number | null;
  /** SKU codes on the form's lines (blank ones are ignored). */
  chosen: readonly string[];
  enabled?: boolean;
}

/**
 * The SKU side of a line editor, without loading a whole catalog: the first
 * page of the client's ACTIVE SKUs (picker list before typing, "client has no
 * SKU" hint, error + retry), chosen codes the form doesn't know yet resolved
 * by `skus=`, and — when a hub is given — the allocatable qty of the chosen
 * SKUs only (`/stocks/availability`), refetched when the chosen set changes.
 * Typed searches live in each row's picker ({@link useSkuSearch}).
 *
 * The return value is memoised: it only changes when the catalog, the
 * availability or the load state does, so line rows can memoise on it.
 */
export function useClientCatalog({
  clientID,
  hubID,
  chosen,
  enabled = true,
}: Options): ClientCatalog {
  const on = enabled && !!clientID;

  // Results are tagged with the client (and hub) they belong to: useLoad
  // keeps the last answer while the next one loads or after it fails, and a
  // previous client's SKUs / stock must never show for this one.
  const firstLoad = useLoad(
    async (signal) => ({
      clientID,
      page: await warehouseApi.listSkus(
        { clientID, status: 'ACTIVE', page: 1, limit: SKU_PAGE_SIZE },
        { signal }
      ),
    }),
    [clientID],
    on
  );
  const first =
    firstLoad.data?.clientID === clientID ? firstLoad.data.page : null;

  // Picked / resolved / created SKUs, plus codes the catalog doesn't have.
  // Both belong to one client — reset when it changes.
  const [known, setKnown] = useState<ReadonlyMap<string, WarehouseSku>>(
    () => new Map()
  );
  const [added, setAdded] = useState<ReadonlySet<string>>(() => new Set());
  const [missing, setMissing] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    setKnown((prev) => (prev.size ? new Map() : prev));
    setAdded((prev) => (prev.size ? new Set() : prev));
    setMissing((prev) => (prev.size ? new Set() : prev));
  }, [clientID]);

  const firstPage = useMemo(() => first?.data ?? NO_SKUS, [first]);

  const skuBy = useMemo(() => {
    const map = new Map<string, WarehouseSku>();
    firstPage.forEach((s) => map.set(s.sku, s));
    known.forEach((s, code) => map.set(code, s));
    return map;
  }, [firstPage, known]);

  const merge = useCallback((skus: WarehouseSku[]) => {
    if (!skus.length) return;
    setKnown((prev) => {
      const next = new Map(prev);
      skus.forEach((s) => next.set(s.sku, s));
      return next;
    });
  }, []);

  // Chosen codes nobody has described yet (seeded lines) → `skus=`. Wait for
  // the first page: it usually knows them already.
  const chosenKey = codeKey(chosen);
  const unresolvedKey = codeKey(
    codesOf(chosenKey).filter((c) => !skuBy.has(c) && !missing.has(c))
  );
  const resolveLoad = useLoad(
    async (signal) => {
      const requested = codesOf(unresolvedKey);
      const found = await warehouseApi.getSkusByCode(
        { clientID: clientID as number, skus: requested },
        { signal }
      );
      return { clientID, requested, found };
    },
    [clientID, unresolvedKey],
    on && !!unresolvedKey && !firstLoad.loading
  );
  useEffect(() => {
    const res = resolveLoad.data;
    if (!res || res.clientID !== clientID) return;
    merge(res.found);
    const got = new Set(res.found.map((s) => s.sku));
    const gone = res.requested.filter((c) => !got.has(c));
    if (gone.length)
      setMissing((prev) => new Set([...Array.from(prev), ...gone]));
  }, [resolveLoad.data, clientID, merge]);

  // Allocatable qty of the chosen SKUs only, refetched per chosen set.
  const availLoad = useLoad(
    async (signal) => ({
      scope: `${hubID}:${clientID}`,
      rows: await warehouseApi.getStockAvailability(
        {
          hubID: hubID as number,
          clientID: clientID as number,
          skus: codesOf(chosenKey),
        },
        { signal }
      ),
    }),
    [clientID, hubID, chosenKey],
    on && !!hubID && !!chosenKey
  );
  // The previous set's numbers stay while a changed set loads (same scope).
  const availRows =
    availLoad.data?.scope === `${hubID}:${clientID}`
      ? availLoad.data.rows
      : null;
  const available = useMemo(() => {
    const map = new Map<string, number>();
    (availRows ?? []).forEach((a) =>
      map.set(a.sku, Math.max(0, Number(a.available) || 0))
    );
    return map;
  }, [availRows]);
  const availableFor = useCallback(
    (sku: string): number | null =>
      hubID ? (available.get(sku) ?? null) : null,
    [hubID, available]
  );

  const remember = useCallback((s: WarehouseSku) => merge([s]), [merge]);
  const addSku = useCallback(
    (s: WarehouseSku) => {
      merge([s]);
      setAdded((prev) => new Set([...Array.from(prev), s.sku]));
      setMissing((prev) => {
        if (!prev.has(s.sku)) return prev;
        const next = new Set(prev);
        next.delete(s.sku);
        return next;
      });
    },
    [merge]
  );

  const empty =
    !!first && !firstLoad.error && first.pagination.total === 0 && !added.size;

  const reloadFirst = firstLoad.reload;
  const reloadResolve = resolveLoad.reload;
  const reloadAvail = availLoad.reload;
  const reload = useCallback(() => {
    reloadFirst();
    reloadResolve();
    reloadAvail();
  }, [reloadFirst, reloadResolve, reloadAvail]);

  const loading = firstLoad.loading || resolveLoad.loading;
  const error = firstLoad.error ?? resolveLoad.error;

  return useMemo(
    () => ({
      clientID,
      firstPage,
      skuBy,
      empty,
      availableFor,
      remember,
      addSku,
      loading,
      error,
      stockError: availLoad.error,
      reload,
    }),
    [
      clientID,
      firstPage,
      skuBy,
      empty,
      availableFor,
      remember,
      addSku,
      loading,
      error,
      availLoad.error,
      reload,
    ]
  );
}

/**
 * One SKU picker's server search over ACTIVE SKUs (an inactive one can't go
 * on a new document): `GET /skus?search=` debounced, and a newer term aborts
 * the request of the older one, so a slow stale answer never replaces the
 * current rows. Nothing is asked before the user types — the catalog's first
 * page ({@link useClientCatalog}) is the list until then.
 */
export function useSkuSearch(
  clientID: number | null,
  enabled = true,
  {
    searchOnly = true,
    pageSize,
  }: {
    /** Default true: nothing is asked until the user types. False opens on
     *  the first `pageSize` active SKUs of the client. */
    searchOnly?: boolean;
    pageSize?: number;
  } = {}
): PagedPicker<WarehouseSku> {
  return usePagedPicker<WarehouseSku>({
    scope: String(clientID),
    enabled: enabled && !!clientID,
    searchOnly,
    pageSize,
    keyOf: skuKey,
    fetchPage: (req, signal) =>
      warehouseApi.listSkus(
        {
          clientID,
          search: req.search,
          status: 'ACTIVE',
          page: req.page,
          limit: req.limit,
        },
        { signal }
      ),
  });
}
